import {
  ArrowLeft,
  LoaderCircle,
  Plus,
  Save,
  Trash2,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { useEmpresa } from '../../context/EmpresaContext'
import { useModalMensaje } from '../../hooks/useModalMensaje'
import { supabase } from '../../lib/supabase'
import {
  formatearCantidadBodega,
  obtenerFechaActualBodega,
} from '../../utils/bodega'
import ModalMensaje from '../ui/ModalMensaje'

const TIPOS_DOCUMENTO_MOVIMIENTO = [
  'Factura',
  'Guía de despacho',
  'Orden de compra',
  'Recepción',
  'Otro',
  'Sin documento',
]

function convertirNumero(valor) {
  const numero = Number.parseFloat(valor)

  return Number.isFinite(numero) ? numero : 0
}

function crearFilaVacia(id) {
  return { id, productoId: '', cantidad: '' }
}

function FormularioMovimientoBodega({
  tipoMovimiento,
  titulo,
  subtitulo,
  onGuardado,
  onCancelar,
}) {
  const { empresaActual } = useEmpresa()
  const { modal, abrirModal } = useModalMensaje()

  const necesitaOrigen =
    tipoMovimiento === 'SALIDA' || tipoMovimiento === 'TRANSFERENCIA'

  const necesitaDestino =
    tipoMovimiento === 'SALDO_INICIAL' ||
    tipoMovimiento === 'ENTRADA' ||
    tipoMovimiento === 'TRANSFERENCIA'

  const restringeStockOrigen = necesitaOrigen

  const permiteDocumento =
    tipoMovimiento === 'ENTRADA' || tipoMovimiento === 'SALIDA'

  const referenciaInicial =
    tipoMovimiento === 'SALDO_INICIAL'
      ? 'Carga inicial de inventario'
      : ''

  const [fecha, setFecha] = useState(obtenerFechaActualBodega())
  const [bodegaOrigenId, setBodegaOrigenId] = useState('')
  const [bodegaDestinoId, setBodegaDestinoId] = useState('')
  const [tipoDocumento, setTipoDocumento] = useState('')
  const [numeroDocumento, setNumeroDocumento] = useState('')
  const [referencia, setReferencia] = useState(referenciaInicial)
  const [observaciones, setObservaciones] = useState('')
  const [detalles, setDetalles] = useState([])

  const [bodegas, setBodegas] = useState([])
  const [productos, setProductos] = useState([])
  const [stockOrigen, setStockOrigen] = useState([])

  const [cargandoBodegas, setCargandoBodegas] = useState(true)
  const [cargandoProductos, setCargandoProductos] = useState(
    !restringeStockOrigen
  )
  const [cargandoStock, setCargandoStock] = useState(false)

  const [guardando, setGuardando] = useState(false)

  const contadorFilaRef = useRef(0)
  const empresaCargadaRef = useRef(null)

  const siguienteFilaId = () => {
    contadorFilaRef.current += 1
    return contadorFilaRef.current
  }

  useEffect(() => {
    if (!empresaActual?.id) return

    if (empresaCargadaRef.current === empresaActual.id) return

    empresaCargadaRef.current = empresaActual.id

    setFecha(obtenerFechaActualBodega())
    setBodegaOrigenId('')
    setBodegaDestinoId('')
    setTipoDocumento('')
    setNumeroDocumento('')
    setReferencia(referenciaInicial)
    setObservaciones('')
    setDetalles([])
    setStockOrigen([])

    cargarBodegas()

    if (!restringeStockOrigen) {
      cargarProductos()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActual?.id])

  const cargarBodegas = async () => {
    if (!empresaActual?.id) return

    try {
      setCargandoBodegas(true)

      const { data, error } = await supabase
        .from('bodegas')
        .select('id, codigo, nombre')
        .eq('empresa_id', empresaActual.id)
        .eq('activo', true)
        .order('codigo')

      if (error) throw error

      setBodegas(data || [])
    } catch (errorCarga) {
      console.error('Error al cargar bodegas:', errorCarga)

      abrirModal({
        tipo: 'error',
        titulo: 'Error al cargar bodegas',
        mensaje:
          errorCarga.message ||
          'No fue posible cargar las bodegas.',
      })
    } finally {
      setCargandoBodegas(false)
    }
  }

  const cargarProductos = async () => {
    if (!empresaActual?.id) return

    try {
      setCargandoProductos(true)

      const { data, error } = await supabase
        .from('productos')
        .select(`
          id,
          codigo,
          nombre,
          unidad_medida_id,
          unidad:unidades_medida!fk_producto_unidad_empresa(
            codigo,
            nombre
          )
        `)
        .eq('empresa_id', empresaActual.id)
        .eq('activo', true)
        .order('codigo')

      if (error) throw error

      setProductos(data || [])
    } catch (errorCarga) {
      console.error('Error al cargar productos:', errorCarga)

      abrirModal({
        tipo: 'error',
        titulo: 'Error al cargar productos',
        mensaje:
          errorCarga.message ||
          'No fue posible cargar los productos.',
      })
    } finally {
      setCargandoProductos(false)
    }
  }

  const cargarStockOrigen = async (idBodegaOrigen) => {
    if (!empresaActual?.id || !idBodegaOrigen) {
      setStockOrigen([])
      return
    }

    try {
      setCargandoStock(true)

      const { data, error } = await supabase
        .from('vw_stock_bodega')
        .select(`
          producto_id,
          producto_codigo,
          producto_nombre,
          producto_activo,
          unidad_codigo,
          saldo
        `)
        .eq('empresa_id', empresaActual.id)
        .eq('bodega_id', idBodegaOrigen)
        .eq('producto_activo', true)
        .gt('saldo', 0)
        .order('producto_codigo')

      if (error) throw error

      setStockOrigen(data || [])
    } catch (errorCarga) {
      console.error(
        'Error al cargar stock de la bodega origen:',
        errorCarga
      )

      abrirModal({
        tipo: 'error',
        titulo: 'Error al cargar stock',
        mensaje:
          errorCarga.message ||
          'No fue posible cargar el stock disponible.',
      })
    } finally {
      setCargandoStock(false)
    }
  }

  useEffect(() => {
    if (!restringeStockOrigen) return

    setDetalles([])
    cargarStockOrigen(bodegaOrigenId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodegaOrigenId, restringeStockOrigen])

  const productosDisponibles = restringeStockOrigen
    ? stockOrigen.map((item) => ({
        id: item.producto_id,
        codigo: item.producto_codigo,
        nombre: item.producto_nombre,
        unidad: item.unidad_codigo,
        disponible: Number(item.saldo) || 0,
      }))
    : productos.map((producto) => ({
        id: producto.id,
        codigo: producto.codigo,
        nombre: producto.nombre,
        unidad: producto.unidad?.codigo || '',
        disponible: null,
      }))

  const obtenerProducto = (productoId) =>
    productosDisponibles.find((item) => item.id === productoId)

  const agregarFila = () => {
    if (restringeStockOrigen && !bodegaOrigenId) {
      abrirModal({
        tipo: 'warning',
        titulo: 'Selecciona una bodega origen',
        mensaje:
          'Debes seleccionar la bodega origen antes de agregar productos.',
      })
      return
    }

    setDetalles((actuales) => [
      ...actuales,
      crearFilaVacia(siguienteFilaId()),
    ])
  }

  const eliminarFila = (id) => {
    setDetalles((actuales) =>
      actuales.filter((fila) => fila.id !== id)
    )
  }

  const cambiarProductoFila = (id, productoId) => {
    const yaAgregado = detalles.some(
      (fila) => fila.id !== id && fila.productoId === productoId
    )

    if (productoId && yaAgregado) {
      abrirModal({
        tipo: 'warning',
        titulo: 'Producto duplicado',
        mensaje: 'El producto ya fue agregado al movimiento.',
      })
      return
    }

    setDetalles((actuales) =>
      actuales.map((fila) =>
        fila.id === id ? { ...fila, productoId } : fila
      )
    )
  }

  const cambiarCantidadFila = (id, cantidad) => {
    setDetalles((actuales) =>
      actuales.map((fila) =>
        fila.id === id ? { ...fila, cantidad } : fila
      )
    )
  }

  const manejarEnvio = async (event) => {
    event.preventDefault()

    if (guardando) return

    if (!fecha) {
      abrirModal({
        tipo: 'error',
        titulo: 'Fecha requerida',
        mensaje: 'Debes ingresar la fecha del movimiento.',
      })
      return
    }

    if (necesitaOrigen && !bodegaOrigenId) {
      abrirModal({
        tipo: 'error',
        titulo: 'Bodega origen requerida',
        mensaje: 'Debes seleccionar la bodega de origen.',
      })
      return
    }

    if (necesitaDestino && !bodegaDestinoId) {
      abrirModal({
        tipo: 'error',
        titulo: 'Bodega destino requerida',
        mensaje: 'Debes seleccionar la bodega de destino.',
      })
      return
    }

    if (
      necesitaOrigen &&
      necesitaDestino &&
      bodegaOrigenId === bodegaDestinoId
    ) {
      abrirModal({
        tipo: 'error',
        titulo: 'Bodegas inválidas',
        mensaje:
          'La bodega de origen y la bodega de destino deben ser distintas.',
      })
      return
    }

    if (detalles.length === 0) {
      abrirModal({
        tipo: 'error',
        titulo: 'Productos requeridos',
        mensaje: 'Debes agregar al menos un producto.',
      })
      return
    }

    for (const fila of detalles) {
      if (!fila.productoId) {
        abrirModal({
          tipo: 'error',
          titulo: 'Producto requerido',
          mensaje: 'Debes seleccionar un producto en cada fila.',
        })
        return
      }

      const cantidad = convertirNumero(fila.cantidad)

      if (cantidad <= 0) {
        abrirModal({
          tipo: 'error',
          titulo: 'Cantidad inválida',
          mensaje:
            'La cantidad de cada producto debe ser mayor que cero.',
        })
        return
      }

      if (restringeStockOrigen) {
        const producto = obtenerProducto(fila.productoId)
        const disponible = producto?.disponible ?? 0

        if (cantidad > disponible) {
          abrirModal({
            tipo: 'error',
            titulo: 'Stock insuficiente',
            mensaje: `Stock insuficiente para "${producto?.nombre}". Disponible: ${formatearCantidadBodega(disponible)} ${producto?.unidad || ''}. Solicitado: ${formatearCantidadBodega(cantidad)} ${producto?.unidad || ''}.`,
          })
          return
        }
      }
    }

    const payload = {
      p_empresa_id: empresaActual.id,
      p_tipo_movimiento: tipoMovimiento,
      p_fecha: fecha,
      p_bodega_origen_id: necesitaOrigen ? bodegaOrigenId : null,
      p_bodega_destino_id: necesitaDestino ? bodegaDestinoId : null,
      p_tipo_documento: permiteDocumento
        ? tipoDocumento || null
        : null,
      p_numero_documento: permiteDocumento
        ? numeroDocumento.trim() || null
        : null,
      p_referencia: referencia.trim() || null,
      p_observaciones: observaciones.trim() || null,
      p_detalles: detalles.map((fila) => ({
        producto_id: fila.productoId,
        cantidad: convertirNumero(fila.cantidad),
      })),
    }

    try {
      setGuardando(true)

      const { error } = await supabase.rpc(
        'registrar_movimiento_bodega',
        payload
      )

      if (error) throw error

      await onGuardado?.()
    } catch (errorGuardado) {
      console.error(
        'Error al registrar movimiento de bodega:',
        errorGuardado
      )

      abrirModal({
        tipo: 'error',
        titulo: 'No se pudo registrar',
        mensaje:
          errorGuardado.message ||
          'No fue posible registrar el movimiento de bodega.',
      })
    } finally {
      setGuardando(false)
    }
  }

  const cargandoInicial =
    cargandoBodegas || (!restringeStockOrigen && cargandoProductos)

  if (cargandoInicial) {
    return (
      <div className="flex min-h-96 items-center justify-center">
        <div className="text-center">
          <LoaderCircle
            size={38}
            className="mx-auto animate-spin text-emerald-600"
          />

          <p className="mt-4 font-medium text-slate-500">
            Cargando formulario...
          </p>
        </div>
      </div>
    )
  }

  const bodegasDestinoDisponibles = necesitaOrigen
    ? bodegas.filter((bodega) => bodega.id !== bodegaOrigenId)
    : bodegas

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={onCancelar}
        className="mb-5 flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-emerald-700"
      >
        <ArrowLeft size={18} />
        Volver a movimientos
      </button>

      <div className="mb-8">
        <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
          {empresaActual?.nombre_fantasia}
        </p>

        <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
          {titulo}
        </h2>

        {subtitulo && (
          <p className="mt-2 text-slate-500">{subtitulo}</p>
        )}
      </div>

      <form
        onSubmit={manejarEnvio}
        className="w-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
      >
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          <Campo label="Fecha" requerido>
            <input
              type="date"
              value={fecha}
              onChange={(event) => setFecha(event.target.value)}
              required
              disabled={guardando}
              className={estiloInput}
            />
          </Campo>

          {necesitaOrigen && (
            <Campo label="Bodega origen" requerido>
              <select
                value={bodegaOrigenId}
                onChange={(event) =>
                  setBodegaOrigenId(event.target.value)
                }
                disabled={guardando}
                className={estiloInput}
              >
                <option value="">Seleccionar bodega</option>

                {bodegas.map((bodega) => (
                  <option
                    key={bodega.id}
                    value={bodega.id}
                  >
                    {bodega.codigo} — {bodega.nombre}
                  </option>
                ))}
              </select>
            </Campo>
          )}

          {necesitaDestino && (
            <Campo label="Bodega destino" requerido>
              <select
                value={bodegaDestinoId}
                onChange={(event) =>
                  setBodegaDestinoId(event.target.value)
                }
                disabled={guardando}
                className={estiloInput}
              >
                <option value="">Seleccionar bodega</option>

                {bodegasDestinoDisponibles.map((bodega) => (
                  <option
                    key={bodega.id}
                    value={bodega.id}
                  >
                    {bodega.codigo} — {bodega.nombre}
                  </option>
                ))}
              </select>
            </Campo>
          )}

          {permiteDocumento && (
            <>
              <Campo label="Tipo de documento">
                <select
                  value={tipoDocumento}
                  onChange={(event) =>
                    setTipoDocumento(event.target.value)
                  }
                  disabled={guardando}
                  className={estiloInput}
                >
                  <option value="">Sin especificar</option>

                  {TIPOS_DOCUMENTO_MOVIMIENTO.map((opcion) => (
                    <option
                      key={opcion}
                      value={opcion}
                    >
                      {opcion}
                    </option>
                  ))}
                </select>
              </Campo>

              <Campo label="Número de documento">
                <input
                  type="text"
                  value={numeroDocumento}
                  onChange={(event) =>
                    setNumeroDocumento(event.target.value)
                  }
                  placeholder="Ejemplo: 1258"
                  disabled={guardando}
                  className={estiloInput}
                />
              </Campo>
            </>
          )}

          <Campo label="Referencia">
            <input
              type="text"
              value={referencia}
              onChange={(event) => setReferencia(event.target.value)}
              placeholder="Referencia del movimiento"
              disabled={guardando}
              className={estiloInput}
            />
          </Campo>

          <div className="md:col-span-2 xl:col-span-3">
            <Campo label="Observaciones">
              <textarea
                value={observaciones}
                onChange={(event) =>
                  setObservaciones(event.target.value)
                }
                rows={3}
                placeholder="Información adicional del movimiento"
                disabled={guardando}
                className={estiloInput}
              />
            </Campo>
          </div>
        </div>

        <div className="mt-8 border-t border-slate-200 pt-6">
          <div className="mb-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Productos
            </h3>

            <button
              type="button"
              onClick={agregarFila}
              disabled={guardando || cargandoStock}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-600 px-4 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              <Plus size={18} />
              Agregar producto
            </button>
          </div>

          {restringeStockOrigen && !bodegaOrigenId && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
              Selecciona una bodega origen para ver el stock
              disponible.
            </p>
          )}

          {restringeStockOrigen &&
            bodegaOrigenId &&
            cargandoStock && (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <LoaderCircle
                  size={16}
                  className="animate-spin"
                />
                Cargando stock disponible...
              </div>
            )}

          {restringeStockOrigen &&
            bodegaOrigenId &&
            !cargandoStock &&
            productosDisponibles.length === 0 && (
              <p className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
                La bodega seleccionada no tiene stock disponible.
              </p>
            )}

          {detalles.length > 0 && (
            <div className="w-full overflow-x-auto">
              <table className="w-full min-w-[640px] text-left text-sm">
                <thead className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="py-2 pr-3">Producto</th>
                    <th className="py-2 pr-3">Unidad</th>
                    {restringeStockOrigen && (
                      <th className="py-2 pr-3">Disponible</th>
                    )}
                    <th className="py-2 pr-3">Cantidad</th>
                    <th className="py-2 pr-3 text-right">
                      Eliminar
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {detalles.map((fila) => {
                    const producto = obtenerProducto(
                      fila.productoId
                    )

                    return (
                      <tr key={fila.id}>
                        <td className="py-3 pr-3">
                          <select
                            value={fila.productoId}
                            onChange={(event) =>
                              cambiarProductoFila(
                                fila.id,
                                event.target.value
                              )
                            }
                            disabled={guardando}
                            className={estiloInput}
                          >
                            <option value="">
                              Seleccionar producto
                            </option>

                            {productosDisponibles.map((item) => (
                              <option
                                key={item.id}
                                value={item.id}
                              >
                                {item.codigo} — {item.nombre}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td className="whitespace-nowrap py-3 pr-3 text-slate-600">
                          {producto?.unidad || '—'}
                        </td>

                        {restringeStockOrigen && (
                          <td className="whitespace-nowrap py-3 pr-3 text-slate-600">
                            {producto
                              ? `${formatearCantidadBodega(
                                  producto.disponible
                                )} ${producto.unidad}`
                              : '—'}
                          </td>
                        )}

                        <td className="py-3 pr-3">
                          <input
                            type="number"
                            min="0"
                            step="0.001"
                            value={fila.cantidad}
                            onChange={(event) =>
                              cambiarCantidadFila(
                                fila.id,
                                event.target.value
                              )
                            }
                            placeholder="0"
                            disabled={guardando}
                            className={`${estiloInput} min-w-[120px]`}
                          />
                        </td>

                        <td className="py-3 pr-3 text-right">
                          <button
                            type="button"
                            onClick={() => eliminarFila(fila.id)}
                            disabled={guardando}
                            title="Eliminar producto"
                            aria-label="Eliminar producto"
                            className="rounded-lg border border-red-200 p-2 text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-col-reverse gap-3 border-t border-slate-200 pt-6 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancelar}
            disabled={guardando}
            className="w-full rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            Cancelar
          </button>

          <button
            type="submit"
            disabled={guardando}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400 sm:w-auto"
          >
            {guardando ? (
              <LoaderCircle
                size={20}
                className="animate-spin"
              />
            ) : (
              <Save size={20} />
            )}

            {guardando ? 'Guardando...' : 'Registrar movimiento'}
          </button>
        </div>
      </form>

      <ModalMensaje {...modal} />
    </div>
  )
}

const estiloInput =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-3 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100'

function Campo({ label, requerido = false, children }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-slate-700">
        {label}

        {requerido && (
          <span className="ml-1 text-red-500">*</span>
        )}
      </label>

      {children}
    </div>
  )
}

export default FormularioMovimientoBodega
